/**
 * Utility to derive consistent UI styling from frame style selection.
 * Ensures buttons, leaderboards, and other elements match the card frame aesthetic.
 */

// Helpers for color manipulation (used by getScorePanelStyles / getFramePreviewStyles)
const hexToRgba = (hex, alpha = 1) => {
  if (!hex) return `rgba(0, 0, 0, ${alpha})`;
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) || 0;
  const g = parseInt(hex.substring(2, 4), 16) || 0;
  const b = parseInt(hex.substring(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const lightenColor = (hex, percent) => {
  if (!hex) return '#FFFFFF';
  hex = hex.replace('#', '');
  const r = Math.min(255, parseInt(hex.substring(0, 2), 16) + Math.round(255 * percent));
  const g = Math.min(255, parseInt(hex.substring(2, 4), 16) + Math.round(255 * percent));
  const b = Math.min(255, parseInt(hex.substring(4, 6), 16) + Math.round(255 * percent));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// Border radius mappings based on frame style
export const getFrameUIProperties = (frameStyle) => {
  const defaults = {
    borderRadius: '0.75rem', // rounded-xl equivalent (12px)
    borderRadiusSmall: '0.5rem', // rounded-lg (8px)
    borderRadiusTiny: '0.375rem', // rounded-md (6px)
    borderStyle: 'solid',
    borderWidth: '2px',
    isPixelated: false,
    isNeon: false,
    isGlass: false,
  };

  switch (frameStyle) {
    // Sharp corners - pixel/retro
    case 'pixel':
      return {
        ...defaults,
        borderRadius: '0',
        borderRadiusSmall: '0',
        borderRadiusTiny: '0',
        borderWidth: '3px',
        isPixelated: true,
      };

    // Slightly rounded - arcade/crt
    case 'arcade':
    case 'crt':
      return {
        ...defaults,
        borderRadius: '0.5rem', // rounded-lg
        borderRadiusSmall: '0.375rem',
        borderRadiusTiny: '0.25rem',
        borderWidth: '3px',
      };

    // Extra rounded - pill shape
    case 'rounded':
      return {
        ...defaults,
        borderRadius: '1.5rem', // rounded-3xl (24px)
        borderRadiusSmall: '1rem', // rounded-2xl (16px)
        borderRadiusTiny: '0.75rem',
      };

    // Glass - extra rounded with blur
    case 'glass':
      return {
        ...defaults,
        borderRadius: '1rem', // rounded-2xl
        borderRadiusSmall: '0.75rem',
        borderRadiusTiny: '0.5rem',
        isGlass: true,
      };

    // Neon - glowy
    case 'neon':
      return {
        ...defaults,
        borderRadius: '0.75rem',
        borderRadiusSmall: '0.5rem',
        borderRadiusTiny: '0.375rem',
        isNeon: true,
      };

    // VHS - slightly rounded
    case 'vhs':
      return {
        ...defaults,
        borderRadius: '0.5rem',
        borderRadiusSmall: '0.375rem',
        borderRadiusTiny: '0.25rem',
      };

    // Dashed/Dotted - keep rounded but special border
    case 'dashed':
      return {
        ...defaults,
        borderStyle: 'dashed',
      };

    case 'dotted':
      return {
        ...defaults,
        borderStyle: 'dotted',
        borderWidth: '3px',
      };

    // Double/Triple frames - slightly less rounded
    case 'double':
    case 'triple':
      return {
        ...defaults,
        borderRadius: '0.5rem',
        borderRadiusSmall: '0.375rem',
        borderRadiusTiny: '0.25rem',
      };

    // Shadow - extra rounded for floating effect
    case 'shadow':
      return {
        ...defaults,
        borderRadius: '1rem',
        borderRadiusSmall: '0.75rem',
        borderRadiusTiny: '0.5rem',
      };

    // Letterbox / Notebook - classic rounded
    case 'letterbox':
    case 'notebook':
      return { ...defaults };

    // Polaroid / Filmstrip - slightly rounded
    case 'polaroid':
    case 'filmstrip':
      return {
        ...defaults,
        borderRadius: '0.25rem',
        borderRadiusSmall: '0.2rem',
        borderRadiusTiny: '0.15rem',
      };

    // Comic - bold like pixel but rounded
    case 'comic':
      return {
        ...defaults,
        borderRadius: '0.5rem',
        borderRadiusSmall: '0.375rem',
        borderRadiusTiny: '0.25rem',
        borderWidth: '3px',
      };

    // Outline / Soft glow - modern rounded
    case 'outline':
    case 'softglow':
      return {
        ...defaults,
        borderRadius: '0.75rem',
        borderRadiusSmall: '0.5rem',
        borderRadiusTiny: '0.375rem',
      };

    // Sticker / Wavy - playful rounded
    case 'sticker':
    case 'wavy':
      return {
        ...defaults,
        borderRadius: '1rem',
        borderRadiusSmall: '0.75rem',
        borderRadiusTiny: '0.5rem',
      };

    // Premium - gold / metallic / spotlight
    case 'gold':
    case 'metallic':
    case 'spotlight':
      return {
        ...defaults,
        borderRadius: '0.75rem',
        borderRadiusSmall: '0.5rem',
        borderRadiusTiny: '0.375rem',
      };

    // Minimal - standard rounded
    case 'minimal':
    case 'bordered':
    case 'gradient':
    case 'clean':
    default:
      return defaults;
  }
};

/**
 * Get button styles that match the frame style
 * Note: Buttons always use solid borders - dashed/dotted only applies to card frames
 */
export const getButtonStyles = (frameStyle, colors = {}) => {
  const uiProps = getFrameUIProperties(frameStyle);
  const primaryAction = colors.primaryAction || '#7C3AED';
  
  const baseStyles = {
    borderRadius: uiProps.borderRadiusSmall,
    borderStyle: 'solid', // Always solid for buttons
    borderWidth: '0',
    transition: 'all 0.2s ease',
  };

  if (uiProps.isPixelated) {
    return {
      ...baseStyles,
      boxShadow: `2px 2px 0 rgba(0,0,0,0.2)`,
      border: `2px solid rgba(0,0,0,0.15)`,
    };
  }

  if (uiProps.isNeon) {
    return {
      ...baseStyles,
      boxShadow: `0 0 6px ${primaryAction}44, 0 0 12px ${primaryAction}22`,
    };
  }

  if (uiProps.isGlass) {
    return {
      ...baseStyles,
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
    };
  }

  return baseStyles;
};

/**
 * Get leaderboard container styles that match the frame style
 */
export const getLeaderboardStyles = (frameStyle, _colors = {}) => {
  const uiProps = getFrameUIProperties(frameStyle);
  
  return {
    borderRadius: uiProps.borderRadiusSmall,
    overflow: 'hidden',
  };
};

/**
 * Get input/form field styles that match the frame style
 * Note: Inputs always use solid borders - dashed/dotted only applies to card frames
 */
export const getInputStyles = (frameStyle) => {
  const uiProps = getFrameUIProperties(frameStyle);
  
  return {
    borderRadius: uiProps.borderRadiusTiny,
    borderStyle: 'solid', // Always solid for inputs
  };
};

/**
 * Get score panel styles (HTML overlay) that match the frame style.
 * Single source of truth for GameScoreDisplay; covers all frame IDs.
 */
export const getScorePanelStyles = (frameStyle, colors = {}) => {
  const primary = colors.primaryAction || '#7C3AED';
  const surface = colors.surface || '#FFFFFF';
  const text = colors.text || '#FFFFFF';
  const secondary = colors.secondaryAction || primary;

  const baseStyles = {
    backgroundColor: hexToRgba(surface, 0.85),
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  };

  switch (frameStyle) {
    case 'neon':
      return { ...baseStyles, border: `2px solid ${primary}`, boxShadow: `0 0 10px ${hexToRgba(primary, 0.5)}, 0 0 20px ${hexToRgba(primary, 0.3)}, inset 0 0 10px ${hexToRgba(primary, 0.1)}`, borderRadius: '8px' };
    case 'pixel':
      return { ...baseStyles, border: `3px solid ${text}`, boxShadow: `4px 4px 0 ${text}`, borderRadius: '0' };
    case 'arcade':
      return { ...baseStyles, border: `3px solid ${primary}`, boxShadow: '0 0 0 2px #1a1a1a, 0 4px 12px rgba(0,0,0,0.3)', borderRadius: '6px' };
    case 'crt':
      return { ...baseStyles, border: '2px solid #1a1a1a', boxShadow: '0 0 0 3px #2a2a2a, 0 4px 12px rgba(0,0,0,0.4)', borderRadius: '8px' };
    case 'vhs':
      return { ...baseStyles, border: '2px solid #e94560', boxShadow: '0 0 10px rgba(233, 69, 96, 0.3)', borderRadius: '4px' };
    case 'glass':
      return { ...baseStyles, backgroundColor: hexToRgba(surface, 0.6), border: '1px solid rgba(255,255,255,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', borderRadius: '12px' };
    case 'gradient':
      return { background: `linear-gradient(135deg, ${hexToRgba(primary, 0.9)}, ${hexToRgba(secondary, 0.9)})`, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', borderRadius: '10px' };
    case 'double':
      return { ...baseStyles, border: `2px solid ${primary}`, boxShadow: `0 0 0 4px ${hexToRgba(secondary, 0.5)}, 0 4px 12px rgba(0,0,0,0.15)`, borderRadius: '8px' };
    case 'triple':
      return { ...baseStyles, border: `2px solid ${lightenColor(primary, 0.2)}`, boxShadow: `0 0 0 3px ${primary}, 0 0 0 5px ${hexToRgba(primary, 0.5)}, 0 4px 12px rgba(0,0,0,0.2)`, borderRadius: '8px' };
    case 'shadow':
      return { ...baseStyles, border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.25), 0 4px 8px rgba(0,0,0,0.15)', borderRadius: '12px' };
    case 'bordered':
      return { ...baseStyles, border: `2px solid ${primary}`, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: '8px' };
    case 'rounded':
      return { ...baseStyles, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: '16px' };
    case 'dashed':
      return { ...baseStyles, border: `2px dashed ${primary}`, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: '8px' };
    case 'dotted':
      return { ...baseStyles, border: `3px dotted ${primary}`, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: '8px' };
    case 'minimal':
      return { ...baseStyles, backgroundColor: hexToRgba(surface, 0.7), border: `1px solid ${hexToRgba(text, 0.1)}`, boxShadow: 'none', borderRadius: '6px' };
    case 'letterbox':
    case 'notebook':
      return { ...baseStyles, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', borderRadius: '10px' };
    case 'polaroid':
    case 'filmstrip':
      return { ...baseStyles, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: '6px' };
    case 'comic':
      return { ...baseStyles, border: `3px solid ${text}`, boxShadow: '0 4px 12px rgba(0,0,0,0.2)', borderRadius: '8px' };
    case 'outline':
    case 'softglow':
      return { ...baseStyles, border: `2px solid ${primary}`, boxShadow: `0 0 12px ${hexToRgba(primary, 0.2)}`, borderRadius: '10px' };
    case 'sticker':
    case 'wavy':
      return { ...baseStyles, border: `2px solid ${primary}`, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: '12px' };
    case 'gold':
    case 'metallic':
    case 'spotlight':
      return { ...baseStyles, border: '2px solid rgba(201, 162, 39, 0.6)', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', borderRadius: '10px' };
    case 'clean':
    default:
      return { ...baseStyles, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', borderRadius: '10px' };
  }
};

/**
 * Minimal styles for the AppearanceSection frame preview box (tiny card thumbnail).
 * Uses getFrameUIProperties so all frame styles get correct radius/border/shadow.
 */
export const getFramePreviewStyles = (frameStyle, colors = {}) => {
  const uiProps = getFrameUIProperties(frameStyle);
  const primary = colors.primaryAction || '#7C3AED';

  const base = {
    borderRadius: uiProps.borderRadiusSmall,
    background: colors.surface || '#FFFFFF',
  };

  if (frameStyle === 'pixel') {
    return { ...base, borderRadius: '0', border: '2px solid #333', boxShadow: '0 1px 4px rgba(0,0,0,0.15)' };
  }
  if (frameStyle === 'neon') {
    return { ...base, boxShadow: `0 0 4px ${primary}` };
  }
  if (frameStyle === 'rounded') {
    return { ...base, borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.15)' };
  }
  return { ...base, boxShadow: '0 1px 4px rgba(0,0,0,0.15)' };
};
